import React, { useEffect, useState } from "react";
import { Box, Card, Divider, Grid, Tab, Paper, Container } from "@material-ui/core";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import CommonTabs from "src/components/CommonTabs/CommonTabs";
import TabHeader from "src/components/TabHeader/TabHeader";
import { NodConfigurationRoute } from "../../configuration.routes";
import { Cargo } from "./components/Cargo/Cargo";
import { Checkpoints } from "./components/Checkpoints/Checkpoints";
import { Consignments } from "./components/Consigments/Consignments";
import { FileTypes } from "./components/FileTypes/FileTypes";
import { TerminalLocation } from "./components/TerminalLocation/TerminalLocation";
import { VehicleStatus } from "./components/VehicleStatus/VehicleStatus";
import { VehicleTypes } from "./components/VehicleTypes/VehicleTypes";
import { formIsDirty, setisDirtyValue } from "src/app-redux/configuration/configurationSlice";
import { BannedItems } from "./components/BannedItems/BannedItems";
import { Supplier } from "./components/SupplierDetails/Supplier";
import { Consignee } from "./components/Consignee/Consignee";

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
    },
    pageHeading: {
      fontSize: theme.typography.pxToRem(20),
      color: "#000",
      lineHeight: "28px",
    },
    breadcrumbs: {
      fontSize: theme.typography.pxToRem(12),
    },
    backdrop: {
      zIndex: theme.zIndex.modal + 1,
      color: "#fff",
    },
    ".hidden": {
      overflow: "hidden",
    },
    accordionsSection: {
      overflow: "auto",
    },
    seeMoreBlock: {
      maxWidth: "150px",
    },
    moreInfoBlock: {
      width: "100%",
      display: "none",
      background: "#f6f6f6",
      position: "absolute",
      top: "40px",
      paddingBottom: "10px",
    },
    ".seeMoreBlock:hover > .moreInfoBlock": {
      background: "red",
    },
    cardMainParent: {
      float: "left",
      width: "100%",
    },
    tabCard: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      boxShadow: "none",
      borderRadius: "0px",
      borderRight: `1px solid ${theme.palette.divider}`,
    },
    tabCardContent: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      boxShadow: "none",
      borderRadius: "0px",
    },
    cardMain: {
      marginBottom: "30px",
      paddingTop: "10px",
      width: "calc(100%)",
    },

    tabs: {
      borderRight: `1px solid ${theme.palette.divider}`,
    },
  })
);

function a11yProps(index: any) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}

type Props = {};
export function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowDimensions;
}
export default function Vehicle({}: Props): React.ReactElement {
  const classes = useStyles();
  const [value, setValue] = useState(0);
  const moduleName = NodConfigurationRoute.title;
  const pageHeading = "Vehicle";
  const isDirty: boolean = useAppSelector(formIsDirty);
  const dispatch = useAppDispatch();
  const [showPrompt, setShowPrompt] = useState<boolean>(true);

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    if (!isDirty) return setValue(newValue);

    // to show prompt on tab change
    if (showPrompt) {
      if (confirm("Changes you made may not be saved.")) {
        dispatch(setisDirtyValue(false));
        return setValue(newValue);
      } else {
        return setShowPrompt(false);
      }
    }
    setShowPrompt(true);
  };

  return (
    <>
      <TabHeader module={moduleName} heading={pageHeading} link={NodConfigurationRoute.path} />
      <Box m={2} />
      <Grid container direction="row">
        <Grid item xs={12} md={12} className={classes.cardMainParent}>
          <Paper elevation={3} className={classes.cardMain}>
            <Box pl={2} pr={2}>
              <Grid container direction="row">
                <Grid item xs={12} md={7}>
                  <Typography className={classes.pageHeading}>{pageHeading}</Typography>
                </Grid>
              </Grid>
            </Box>
            <Box m={1}>
              <Divider />
            </Box>

            <Grid container>
              <Grid item xs={4} md={3} lg={2} component={Card}>
                <div className="configTabs">
                  <CommonTabs value={value} handleChange={handleChange} tabMinHeight={800}>
                    <Tab label="Vehicle Types" {...a11yProps(0)} />
                    <Tab label="Vehicle Status" {...a11yProps(1)} />
                    <Tab label="Type of Consignments" {...a11yProps(2)} />
                    <Tab label="Location at Terminal" {...a11yProps(3)} />
                    <Tab label="Type of Cargo" {...a11yProps(4)} />
                    <Tab label="File Types" {...a11yProps(5)} />
                    <Tab label="Check Points" {...a11yProps(6)} />
                    <Tab label="Banned Items" {...a11yProps(7)} />
                    <Tab label="Supplier Details" {...a11yProps(8)} />
                    <Tab label="Consignee" {...a11yProps(9)} />
                    {/* <Tab label="Units" {...a11yProps(10)} /> */}
                  </CommonTabs>
                </div>
              </Grid>
              <Grid item xs={8} md={9} lg={10} component={Card} className={classes.tabCardContent}>
                <Container className="configSubTabs" style={{ padding: "15px" }}>
                  <VehicleTypes value={value} index={0} />
                  <VehicleStatus value={value} index={1} />
                  <Consignments value={value} index={2} />
                  <TerminalLocation value={value} index={3} />
                  <Cargo value={value} index={4} />
                  <FileTypes value={value} index={5} />
                  <Checkpoints value={value} index={6} />
                  <BannedItems value={value} index={7} />
                  <Supplier value={value} index={8} />
                  <Consignee value={value} index={9} />
                  {/* <Units value={value} index={10} /> */}
                </Container>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
}
